import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";
import { Actions } from "@/store/enums/StoreEnums";
import { dashboardService } from "@/modern/services/dashboardService";
import type { IUser, IMachine, INextEvent, IPublicUser } from "@/modern/types/dashboard";

const emptyMachine: IMachine = {
  id: 0,
  name: "",
  photo_path: "",
  dificulty: "",
  total_flags: 0,
  os_name: "",
  xp: 0,
  type: "",
  is_freemium: false,
  percentage: 0,
  release_at: "",
  is_free_week_rotation: false,
  free_week_rotation_starts_at: null,
  free_week_rotation_ends_at: null
};

interface IDiscount {
  brl: number;
  usd: number;
}

interface IPlan {
  identifier: string;
  value_cents: number;
  value_dolar_cents: number;
  final_value_cents: number;
  final_value_dolar_cents: number;
}

export type IProgressiveDiscount =
  | {
      has_discount: true;
      discount: IDiscount;
      plan: IPlan;
    }
  | {
      has_discount: false;
      discount: null;
      plan: null;
    };

type IProgressiveDiscountWithValue = Extract<IProgressiveDiscount, { has_discount: true }>;

const isProgressiveDiscountWithValue = (value: unknown): value is IProgressiveDiscountWithValue => {
  const candidate = value as IProgressiveDiscountWithValue;

  return (
    candidate?.has_discount === true &&
    candidate.discount !== null &&
    candidate.plan !== null &&
    typeof candidate.plan?.value_cents === "number" &&
    typeof candidate.plan?.final_value_cents === "number"
  );
};

const normalizeProgressiveDiscount = (value: unknown): IProgressiveDiscount | null => {
  if (isProgressiveDiscountWithValue(value)) {
    return value;
  }

  return null;
};

export const useDashboardPage = () => {
  const store = useStore();

  const loading = ref(false);
  const lastMachine = ref<IMachine>(emptyMachine);
  const freeWeekMachine = ref<IMachine>(emptyMachine);
  const nextEvent = ref<INextEvent | null>(null);
  const progressiveDiscount = ref<IProgressiveDiscount | null>(null);

  const user = computed<IUser>(() => (store.getters.currentUser ?? {}) as IUser);
  const userPublic = computed<IPublicUser>(() => (store.getters.publicUser ?? {}) as IPublicUser);
  const courses = computed(() => store.getters.getCourseData);
  const currentLanguage = computed<string>(
    () => store.getters.getCurrentLanguage || localStorage.getItem("currentLanguage") || "en"
  );

  const initialize = async () => {
    loading.value = true;

    const userId = user.value?.id || "me";

    try {
      await Promise.all([
        store.dispatch(Actions.GET_ALL_COURSES),
        store.dispatch(Actions.GET_PUBLIC_USER, { id: userId }),
      ]);
    } catch (error) {
      // Keep parity with legacy behavior: non-blocking fetch failures.
      console.error("Error loading dashboard store data:", error);
    }



    try {
      const [lastMachineResult, freeWeekResult, nextEventResult, progressiveDiscountResult] = await Promise.allSettled([
        dashboardService.fetchLastMachine(),
        dashboardService.fetchFreeWeekMachine(),
        dashboardService.fetchNextEvent(),
        dashboardService.fetchProgressiveDiscount()
      ]);

      lastMachine.value = lastMachineResult.status === "fulfilled" ? lastMachineResult.value : emptyMachine;
      freeWeekMachine.value = freeWeekResult.status === "fulfilled" ? freeWeekResult.value : emptyMachine;
      nextEvent.value = nextEventResult.status === "fulfilled" ? nextEventResult.value : null;
      progressiveDiscount.value =
        progressiveDiscountResult.status === "fulfilled"
          ? normalizeProgressiveDiscount(progressiveDiscountResult.value)
          : null;
    } catch (error) {
      lastMachine.value = emptyMachine;
      freeWeekMachine.value = emptyMachine;
    } finally {
      loading.value = false;
    }
  };

  onMounted(() => {
    void initialize();
  });

  return {
    loading,
    user,
    userPublic,
    courses,
    currentLanguage,
    lastMachine,
    freeWeekMachine,
    nextEvent,
    progressiveDiscount,
  };
};
